import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { parseDay } from '@/lib/time';

// Filtros de la bitácora sincronizados con la URL (deep-linking, UX-NAV-02): el
// estado de la vista vive en searchParams, así recargar o compartir el enlace
// reproduce exactamente la misma consulta. El cursor de paginación NO va a la URL
// (es estado efímero de scroll; lo maneja useInfiniteQuery).

export type CategorySegment = 'todos' | 'flujo' | 'firmas' | 'edicion' | 'accesos' | 'sesion';

const SEGMENTS: CategorySegment[] = ['todos', 'flujo', 'firmas', 'edicion', 'accesos', 'sesion'];

/** Segmento de la vista → categoría real de audit_logs en el backend. */
const SEGMENT_CATEGORY: Record<Exclude<CategorySegment, 'todos'>, string> = {
  flujo: 'document-flow',
  firmas: 'signature',
  edicion: 'online-editing',
  accesos: 'authz',
  sesion: 'auth',
};

export interface UiFilters {
  segment: CategorySegment;
  from: string;
  to: string;
}

export interface QueryFilters {
  category?: string;
  occurredFrom?: string;
  occurredTo?: string;
}

export function parseSegment(raw: string | null): CategorySegment {
  return SEGMENTS.includes(raw as CategorySegment) ? (raw as CategorySegment) : 'todos';
}

export function segmentToCategory(segment: CategorySegment): string | undefined {
  return segment === 'todos' ? undefined : SEGMENT_CATEGORY[segment];
}

/** Solo una fecha real (mismas reglas que el backend: AAAA-MM-DD existente, años
 *  2000-2999) viaja al backend; una fecha basura en la URL o un año a medio teclear
 *  se ignora en vez de producir un 400. */
function validDate(raw: string): string | undefined {
  return parseDay(raw) ?? undefined;
}

/** Traduce los filtros de la vista a los parámetros del endpoint. Puro y
 *  testeable sin router; omite las claves vacías o inválidas (no manda basura). */
export function toQueryFilters(filters: UiFilters): QueryFilters {
  const a = validDate(filters.from);
  const b = validDate(filters.to);
  // Un rango invertido (deep-link o escritura manual) se ordena: AAAA-MM-DD compara
  // como texto en orden cronológico.
  const [from, to] = a !== undefined && b !== undefined && a > b ? [b, a] : [a, b];
  return {
    ...(segmentToCategory(filters.segment) ? { category: segmentToCategory(filters.segment) } : {}),
    ...(from ? { occurredFrom: from } : {}),
    ...(to ? { occurredTo: to } : {}),
  };
}

/** ¿La consulta está acotada? Solo cuenta lo que llega al backend (una fecha inválida
 *  se ignora): distingue "no hay eventos" de "ninguno coincide" (QA F1 #9). */
export function isFiltered(filters: UiFilters): boolean {
  return Object.keys(toQueryFilters(filters)).length > 0;
}

export interface UseTraceFilters {
  filters: UiFilters;
  setSegment: (s: CategorySegment) => void;
  setRange: (from: string, to: string) => void;
  /** Quita el tipo y el rango de fechas. */
  clearAll: () => void;
}

export function useTraceFilters(): UseTraceFilters {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<UiFilters>(
    () => ({
      segment: parseSegment(params.get('tipo')),
      from: params.get('desde') ?? '',
      to: params.get('hasta') ?? '',
    }),
    [params],
  );

  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value === null || value === '') {
              p.delete(key);
            } else {
              p.set(key, value);
            }
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    filters,
    setSegment: (s) => patch({ tipo: s === 'todos' ? null : s }),
    setRange: (from, to) => patch({ desde: from, hasta: to }),
    clearAll: () => patch({ tipo: null, desde: null, hasta: null }),
  };
}
